import { Block } from "@components/block";
import { Config, DoubleText, Table } from "@components/table";
import { useBoolState } from "@hooks/use-bool-state";
import { usePagination } from "@hooks/use-pagination";
import { MerchantWithdrawalModal } from "./modal";
import { useEffect, useState } from "react";
import { useMerchantPayoutOrders } from "@features/merchantPayout";
import {
  MerchantOrderPayout,
  MerchantOrderPayoutRequest,
} from "@features/merchantPayout/api";
import { EllipsisText } from "@components/ellipsis-text";
import { formatDate, formatTime } from "@helpers/date";
import { formatRubles, formatUsdt } from "@helpers/format";
import { getBanks } from "@features/bank/api";
import { SelectOption } from "@components/select";
import { FilterConfig } from "@components/filter-modal";
import { useUrlFilters } from "@hooks/useUrlFilters";
import { StatusBlock, StatusBlockType } from "@components/status-block";
import { useWebSocket } from "@request/useWebsocket";
import { WebsocketUpdateType } from "@request/websocket";
import { useCashRegisters } from "@features/cash-registers";

const statusOptions = [
  { text: "Создано", value: "created" },
  { text: "В процессе", value: "processing" },
  { text: "Зачислен", value: "completed" },
  { text: "Отменена", value: "canceled" },
  { text: "Спор открыт", value: "dispute_open" },
];

const config: Config<MerchantOrderPayout> = [
  {
    label: "Номер заявки",
    source: "invid",
    renderComponent: (data) => <EllipsisText text={data} copy />,
  },
  {
    label: "Дата и время создания ",
    source: "created_at_unix",
    renderComponent: (data) => (
      <DoubleText
        text={formatTime(new Date(data * 1000))}
        subText={formatDate(new Date(data * 1000))}
      />
    ),
  },
  {
    label: "Дата и время закрытия",
    source: "closed_time_unix",
    renderComponent: (data) =>
      data != null && (
        <DoubleText
          text={formatTime(new Date(data * 1000))}
          subText={formatDate(new Date(data * 1000))}
        />
      ),
  },
  {
    label: "Статус заявки",
    source: "status",
    renderComponent: (data) => {
      let text = "";
      let status: StatusBlockType = StatusBlockType.Error;
      switch (data) {
        case "created":
          text = "Создана";
          status = StatusBlockType.Grey;
          break;
        case "completed":
          text = "Зачислен";
          status = StatusBlockType.Success;
          break;
        case "dispute_open":
          text = "Открыт спор";
          status = StatusBlockType.Orange;
          break;
        case "processing":
        case "pending":
        case "assigned_to_trader":
          text = "В обработке";
          status = StatusBlockType.Warning;
          break;
        case "under_review_by_support":
          text = "В обработке";
          status = StatusBlockType.Warning;
          break;
        case "failed":
        case "canceled":
          text = "Отменена";
          status = StatusBlockType.Error;
          break;
      }
      return <StatusBlock text={text} type={status} />;
    },
  },
  { label: "Способ", source: "method" },
  { label: "Реквизиты", source: "requisite" },
  { label: "Банк", source: "bank" },
  {
    label: "Сумма ",
    source: "sum",
    renderComponent: (data, item) => (
      <DoubleText
        text={formatRubles(data)}
        subText={formatUsdt(item.sum_usdt)}
      />
    ),
  },
  {
    label: "Сумма к списанию",
    source: "merchant_spend_rub",
    renderComponent: (data, item) => (
      <DoubleText
        text={formatRubles(data)}
        subText={formatUsdt(item.merchant_spend_usdt)}
      />
    ),
  },
];

export const WithdrawalsPage = () => {
  const [isOpen, open, close] = useBoolState(false);
  const { setParams } = useUrlFilters();
  const { page, limit, setPage, setLimit } = usePagination();
  const { state, getMerchantPayoutOrders } = useMerchantPayoutOrders();
  const [selected, setSelected] = useState<MerchantOrderPayout>();
  const [bankOptions, setBankOptions] = useState<SelectOption[]>([]);
  const { lastMessage } = useWebSocket();
  const [cashOptions, setCashOptions] = useState<SelectOption[]>([]);
  const { state: cacheState, getCashRegisters } = useCashRegisters();

  useEffect(() => {
    getCashRegisters();
  }, [getCashRegisters]);
  useEffect(() => {
    if (cacheState.caches) {
      setCashOptions(
        cacheState.caches.map((cash) => ({ text: cash.name, value: cash.id }))
      );
    }
  }, [cacheState]);

  useEffect(() => {
    if (lastMessage && lastMessage.type == WebsocketUpdateType.PayOut) {
      getMerchantPayoutOrders({ page, limit });
    }
  }, [lastMessage, page, limit]);

  useEffect(() => {
    getBanks().then((banks) => {
      const options = banks.map((bank) => ({
        text: bank.name,
        value: bank.id,
      }));

      setBankOptions(options);
    });
  }, []);
  useEffect(() => {
    getMerchantPayoutOrders({ page, limit });
  }, [getMerchantPayoutOrders, page, limit]);
  const methodOptions: SelectOption[] = [
    { text: "СБП", value: "sbp" },
    { text: "Карта", value: "card" },
    { text: "Счет", value: "bank_transfer" },
  ];

  const filterConfig: FilterConfig<MerchantOrderPayoutRequest> = [
    {
      type: "select",
      options: methodOptions,
      name: "method",
      label: "Метод",
    },
    {
      type: "select",
      options: cashOptions,
      label: "Касса",
      placeholder: "Касса",
      name: "cache_id",
    },
    {
      type: "sum",
      minName: "min_sum",
      maxName: "max_sum",
    },
    {
      type: "select",
      options: bankOptions,
      name: "bank",
      label: "Банк",
    },
    {
      type: "select",
      options: statusOptions,
      name: "status",
      label: "Статус",
    },
    {
      type: "date",
      withTime: true,
      nameStart: "date_start",
      nameEnd: "date_end",
      returnValue: "number",
    },
  ];

  const onFilterSubmit = (data: MerchantOrderPayoutRequest) => {
    setParams(data);
    getMerchantPayoutOrders(data);
  };
  const onClick = (data: MerchantOrderPayout) => {
    setSelected(data);
    open();
  };
  return (
    <Block>
      <Table
        title="Выводы"
        config={config}
        data={state.payoutOrders}
        loading={state.loading}
        withFilter
        filterProps={{
          filterConfig,
          // validationRules: validationRules,
          onSubmit: onFilterSubmit,
        }}
        setPage={setPage}
        setLimit={setLimit}
        page={page}
        limit={limit}
        limits={[100, 200, 300]}
        totalCount={state.totalCount}
        onRowClick={onClick}
      />
      {selected && (
        <MerchantWithdrawalModal isOpen={isOpen} hide={close} data={selected} />
      )}
    </Block>
  );
};
